import { createStyles, Grid, makeStyles } from "@material-ui/core";
import { AutocompleteInputChangeReason } from "@material-ui/lab";
import classnames from "classnames";
import * as React from "react";

import { RelodeTheme, SelectInput, useDebounce, useRelodeApp } from "..";
import { TeamSuggestion } from "./types";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            downshift: {
                margin: theme.spacing(0, 0, 4, 0),
            },
            menuEmptyMessage: {
                height: "auto",
                whiteSpace: "initial",
            },
            paper: {
                overflow: "visible",
            },
            menuItemDetails: {
                whiteSpace: "normal",
                height: "auto",
                display: "block",
            },
        }),
    { name: "teamSuggestionInput" }
);

interface TeamSuggestProps {
    className?: string;
    id: string;
    label: string;
    disabled?: boolean;
    onTeamSelected: (team?: TeamSuggestion) => void;
    selectedTeam: TeamSuggestion;
    error?: boolean;
    helperText?: string;
}

const getOptionLabel = (option: TeamSuggestion) => `${option?.teamName ?? ""}`;
const getOptionDetails = (option: TeamSuggestion): string =>
    [option.location?.locationName, option.companyUrl]
        .filter(o => !!o)
        .join(" • ");

export const TeamSuggest = (props: TeamSuggestProps) => {
    const {
        id,
        label,
        onTeamSelected,
        className,
        disabled = false,
        selectedTeam,
    } = props;
    const classes = useStyles({});
    const rootClasses = classnames(classes.root, className);
    const {
        state: {
            config: { teamLookupService },
        },
    } = useRelodeApp();

    const [teamSuggestions, setTeamSuggestions] = React.useState<
        TeamSuggestion[]
    >();
    const [, setInputValue] = React.useState(getOptionLabel(selectedTeam));
    const loadTeamSuggestion = useDebounce(
        async (searchText: string) => {
            setTeamSuggestions(
                await teamLookupService.loadSuggestions(searchText)
            );
        },
        500,
        []
    );

    const handleLoadSuggestion = async (
        event: never,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (reason === "reset") return;
        if (!value || value.trim() === "" || value.trim().length === 0) {
            setTeamSuggestions([]);
            onTeamSelected(undefined);
            return;
        }
        setInputValue(value);
        await loadTeamSuggestion(value);
    };

    const handleTeamChange = (e: never, newValue: TeamSuggestion) => {
        onTeamSelected(newValue);
    };

    return (
        <div className={rootClasses}>
            <SelectInput
                mode="suggest"
                clearable
                disabled={disabled}
                error={props.error}
                getOptionLabel={getOptionLabel}
                helperText={props.helperText}
                id={id}
                label={label}
                noOptionsText="Your keywords did not match any team."
                onChange={handleTeamChange}
                onInputChange={handleLoadSuggestion}
                options={teamSuggestions ?? []}
                renderOption={(o, s) => (
                    <Grid container>
                        <Grid item xs={12}>
                            {getOptionLabel(o)}
                        </Grid>
                        <Grid item xs={12} style={{ fontSize: 14 }}>
                            {getOptionDetails(o)}
                        </Grid>
                    </Grid>
                )}
                value={selectedTeam}
            />
        </div>
    );
};
